import pick from "lodash/pick";
import omit from "lodash/omit";
import { fetchStoreBasicDetails } from "@graphql/queries/stores/basic-details";

import { getSHA1 } from "@stadium/services/crypto";

import { removeTrailingSlash } from "@stadium/helpers/routes";
import {
  getWorkspaceSlugFromStoreSlug,
  getStoreSlugFromUrlWithWorkspace,
} from "@stadium/helpers/store";
import { rewritePersonalWorkspacePathInUrl } from "@helpers/workspace";

import { WORKSPACE_SLUG_REQUEST_HEADER_KEY } from "@stadium/constants";
import { APP_DOMAIN, APP_ENV, isDev } from "@constants/env-variables";
import {
  ACCOUNT_STORE_TYPE,
  DEMO_MODE_STORE_CURRENCY_OPTIONS,
  ESHOP_STORE_TYPE,
  MOMENT_STORE_TYPE,
  POINTS_CURRENCY_CODE,
  US_CURRENCY_CODE,
  KIT_STORE_TYPE,
  CATALOG_COLLECTION_STORE_TYPE,
  OPEN_ORDER_PREFIX_KEY,
} from "@constants/store";
import { SWAGMAGIC_INTENT } from "@constants/menu";
import { SHOP_LOGO_TYPE } from "@constants/logo";
import { DEFAULT_COUNTRY_SLUG, DEFAULT_LANG_SLUG } from "@constants/app-defaults";

const DEFAULT_OBJ = {};
const DEFAULT_STORE_LAYOUT_PROPS = ["accessible", "externalId"];

export const getStorePreviewModeKey = (key) => {
  return `preview-eshop-${key}`;
};
export const getStorePreviewModeCartKey = (key, country = "", isSendToMyself = false) => {
  if (!isSendToMyself) return `preview-eshop-${key}-send-to-many-cart`;
  return `preview-eshop-${key}-${country.toLowerCase()}-cart`;
};

export const getStorePreviewModeCurrencyKey = (key) => {
  return `preview-eshop-${key}-currency`;
};

export const getStoreOpenOrderNumberKey = (key) => {
  return `${OPEN_ORDER_PREFIX_KEY}-${key}`;
};

export const getDummyStoreKey = (key) => {
  return `dummy-store-id-${key}`;
};

export const getDummyStoreReturnUrlKey = (key) => {
  return `dummy-store-reutrn-url-${key}`;
};

export const getDraftSwagKey = (key) => {
  return `draft-swag-id-${key}`;
};

export const getStoreDomain = () => {
  return !isDev() ? window.location.host : "";
};

export const isValidLogoImageType = (logoType) => {
  return !!SHOP_LOGO_TYPE[logoType];
};

export const getFetchStoreBasicDetailsVariables = ({
  host = "",
  code = "",
  storeNumber = "",
  query = fetchStoreBasicDetails,
  withTheme = false,
}) => {
  const customDomainRoot = !isDev() ? host : "";
  const variables = {
    code,
    customDomainRoot,
    onlyActive: false,
    privateAttributes: false,
    number: storeNumber,
  };

  return {
    query,
    variables: { ...variables, withTheme },
    fetchPolicy: "no-cache",
  };
};

export const getShopUrlOnServerSide = ({ host, resolvedUrl }) => {
  let url = "";
  try {
    const urlObj = new URL(resolvedUrl);
    urlObj.search = "";
    urlObj.hash = "";
    url = urlObj.toString();
  } catch (e) {
    // url is not fully qualified -- fallback value
    const pathname = resolvedUrl.split("?")[0];

    url = `${isDev() ? "http" : "https"}://${host}${pathname}`;
  }
  return rewritePersonalWorkspacePathInUrl(removeTrailingSlash(url));
};

export const getShopResolvedUrl = ({ resolvedUrl }) => {
  try {
    const urlObj = new URL(resolvedUrl);
    return urlObj.toString();
  } catch (e) {
    // url is not fully qualified -- fallback value
    return `${isDev() ? "http" : "https"}://${APP_DOMAIN}${resolvedUrl}`;
  }
};

export const getIsKitShop = (shopType) =>
  [KIT_STORE_TYPE, CATALOG_COLLECTION_STORE_TYPE].includes(shopType);

export const resolveStorePageData = ({ data, slug, extractTheme = false }) => {
  const { shopType } = data?.store || {};

  const isKitShop = getIsKitShop(shopType);
  const store = {
    ...data?.store,
    isKitShop,
    ...(slug && { slug, code: getStoreSlugFromUrlWithWorkspace(slug) }),
  };

  if (extractTheme) {
    return {
      store: omit(store, "brandingTheme"),
      brandingTheme: store?.brandingTheme,
    };
  }

  return store;
};

export const getHrefLangAttributes = ({
  shopUrl,
  resolvedUrl,
  host,
  countryCode,
  lang,
  isNewRoute = false,
}) => {
  const defaultShopUrlPathname = isNewRoute
    ? resolvedUrl.replace(
        `/${countryCode}-${lang}`,
        `/${DEFAULT_COUNTRY_SLUG}-${DEFAULT_LANG_SLUG}`,
      )
    : resolvedUrl.replace(
        `/${countryCode}/${lang}`,
        `/${DEFAULT_COUNTRY_SLUG}/${DEFAULT_LANG_SLUG}`,
      );

  const defaultShopUrl = rewritePersonalWorkspacePathInUrl(
    removeTrailingSlash(getShopResolvedUrl({ resolvedUrl: defaultShopUrlPathname })),
  );

  return {
    query: { countryCode, lang, isNewRoute },
    defaultShopUrl,
    shopUrl,
    host,
  };
};

export const getDefaultPreviewModeCurrency = (storeCurrency) => {
  return storeCurrency === US_CURRENCY_CODE
    ? DEMO_MODE_STORE_CURRENCY_OPTIONS[US_CURRENCY_CODE]
    : DEMO_MODE_STORE_CURRENCY_OPTIONS[POINTS_CURRENCY_CODE];
};

export const isMomentStore = (storeType) => storeType === MOMENT_STORE_TYPE;
export const isEshopStore = (storeType) => storeType === ESHOP_STORE_TYPE;
export const isAccountStore = (storeType) => storeType === ACCOUNT_STORE_TYPE;

export const hideAccountStoreDetails = (storeType, momentStoreVal, eshopStoreVal) => {
  if (isMomentStore(storeType)) {
    return momentStoreVal;
  } else if (isEshopStore(storeType)) {
    return eshopStoreVal;
  }
  return "";
};

export const getCommonGlobalCountryVariables = ({
  country,
  isGlobalCountry,
  countryKeyAttr = "country",
}) => ({
  [countryKeyAttr]: isGlobalCountry ? "" : country?.iso,
  globalZone: isGlobalCountry,
});

export const getMenuItemsCountry = ({ isSwagCreationShopFlow, country, isGlobalCountry }) => {
  const countryCode = isGlobalCountry ? "" : country?.iso;
  return isSwagCreationShopFlow ? "" : countryCode;
};

export const getLockerShopESQueryVars = ({ isLockerShop }) => {
  if (isLockerShop) {
    return {
      intent: SWAGMAGIC_INTENT,
    };
  }
  return {};
};

export const getDefaultStoreLayoutProps = (store) => {
  return pick(store, DEFAULT_STORE_LAYOUT_PROPS);
};

export const getDefaultKitStoreProps = (isKitStore) =>
  isKitStore
    ? {
        withMenu: false,
        withSearch: false,
        withCart: false,
        showRewardPoints: false,
      }
    : DEFAULT_OBJ;

const KEY_PREFIX = `${APP_ENV}-sw-graphql-cache:graphql`;

export const queryKeyGenerator = (query, variables, prefix = KEY_PREFIX) => {
  const variableString = JSON.stringify(
    Object.keys(variables)
      .sort()
      .reduce((acc, k) => {
        acc[k] = variables[k];
        return acc;
      }, {}),
  );
  const str = (variableString + "/" + query).replaceAll(/\s+/g, "");
  const sha1 = getSHA1(str);
  return `${prefix}/${sha1}`;
};

export const getResolvedWorkspaceSlug = ({
  workspaceSlug = "",
  slug = "",
  storeIdentifier = "",
} = DEFAULT_OBJ) => {
  const storeSlug = slug || storeIdentifier;
  return workspaceSlug || getWorkspaceSlugFromStoreSlug(storeSlug);
};

export const getStoreCacheKey = (query, variables, workspaceSlug) =>
  `${queryKeyGenerator(query, variables)}/store-${
    variables?.number || variables?.code
  }-${workspaceSlug}`;

export const getProductCacheKey = (query, variables, workspaceSlug) =>
  `${queryKeyGenerator(query, variables)}/store-product-${variables.storeNumber}-product-${
    variables.slug
  }-${workspaceSlug}`;

export const getWorkspaceHeaders = (workspaceSlug) => {
  return {
    [WORKSPACE_SLUG_REQUEST_HEADER_KEY]: workspaceSlug,
  };
};
